/**
 * SmartDropHandler - Intelligently processes dropped files based on type
 */

import { parseManuscriptFilename, type ParsedManuscript } from './manuscriptParser'

export interface InferredMetadata {
    filename: string
    chapter?: string
    siglum?: string
    library?: string
    description?: string
    parsedData?: ParsedManuscript
}

export type FileType = 'book-json' | 'chapter-json' | 'witness-json' | 'raw-text' | 'unknown'

export interface FileAnalysis {
    type: FileType
    file: File
    inferredMetadata?: InferredMetadata
}

/**
 * Parses filename using the robust manuscriptParser utility
 * Extracts chapter, siglum, and builds library/description
 */
export function parseFilename(filename: string): InferredMetadata {
    const parsedData = parseManuscriptFilename(filename)

    const metadata: InferredMetadata = {
        filename,
        parsedData
    }

    if (parsedData.isValid) {
        metadata.chapter = parsedData.chapter
        metadata.siglum = parsedData.siglum

        // Build description from siglum (first part is usually the library)
        const siglumParts = parsedData.siglum.split(' ')
        if (siglumParts.length > 0) {
            metadata.library = siglumParts[0]
            metadata.description = parsedData.siglum
        }
    }

    return metadata
}

/**
 * Analyzes a file to determine its type
 */
export async function analyzeFile(file: File): Promise<FileAnalysis> {
    const analysis: FileAnalysis = {
        type: 'unknown',
        file,
    }

    // Check if it's JSON
    if (file.name.endsWith('.json')) {
        try {
            const content = await file.text()
            const data = JSON.parse(content)

            // Determine JSON type based on export_type (v2.0.0 format)
            if (data.export_type === 'scholarly_book_v2' || data.export_type === 'scholarly_book_v1') {
                analysis.type = 'book-json'
            } else if (data.export_type === 'scholarly_chapter_v2') {
                analysis.type = 'chapter-json'
            } else if (data.export_type === 'manuscript_witness_v2') {
                analysis.type = 'witness-json'
            } else if (data.export_type === 'workflow_session_v2' || data.export_type === 'text_alignment_v2') {
                // These are also valid JSON imports, treat as generic JSON
                analysis.type = 'witness-json' // Will be handled by importData
            }
        } catch (e) {
            console.error('Failed to parse JSON:', e)
        }
    }
    // Check if it's a raw text file (.txt, .docx, .rtf)
    else if (file.name.match(/\.(txt|rtf|docx)$/i)) {
        analysis.type = 'raw-text'
        analysis.inferredMetadata = parseFilename(file.name)
    }

    return analysis
}

/**
 * Processes multiple files and returns their analyses
 */
export async function analyzeFiles(files: FileList | File[]): Promise<FileAnalysis[]> {
    const fileArray = Array.from(files)
    const analyses = await Promise.all(fileArray.map(analyzeFile))
    return analyses
}
